import { getBackendUrl, resolveAssetUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import SeoHead from '../components/SeoHead';

const MentionsLegalesPage = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState(null);
  const [platformName, setPlatformName] = useState('Talent Partner');

  useEffect(() => {
    // Fetch branding
    fetch(getBackendUrl() + '/api/branding')
      .then(res => res.json())
      .then(data => {
        if (data.logo_light_url) setLogoUrl(data.logo_light_url);
        if (data.platform_name) setPlatformName(data.platform_name);
      })
      .catch(err => console.error('Failed to fetch branding:', err));

    // Fetch Mentions Légales content
    fetch(getBackendUrl() + '/api/legal/mentions_legales')
      .then(res => res.json())
      .then(data => {
        setContent(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch mentions légales:', err);
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-tp-bg">
      {/* SEO Meta Tags */}
      <SeoHead pageTitle={content?.title ? `${content.title} | Talent Partner` : 'Mentions Légales | Talent Partner'} />
      
      {/* Simple Navigation */}
      <nav className="bg-white border-b border-tp-border" data-testid="legal-navigation">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2 text-sm text-tp-text-light hover:text-tp-primary transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Retour au site
            </Link>
            
            <Link to="/" className="flex items-center">
              {logoUrl ? (
                <img 
                  src={resolveAssetUrl(logoUrl)}
                  alt={platformName}
                  className="h-7 max-w-[140px] object-contain"
                />
              ) : (
                <span className="text-lg font-display font-semibold text-tp-primary">
                  {platformName}
                </span>
              )}
            </Link>
            
            <div className="w-[100px]"></div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="flex-1 py-12" data-testid="mentions-legales-page">
        <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-tp-text-light" />
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-tp-border p-4 sm:p-8 md:p-12">
              <h1 className="text-2xl sm:text-3xl font-display font-semibold text-tp-text mb-8 break-words">
                {content?.title || 'Mentions Légales'}
              </h1>

              <div className="prose prose-slate max-w-none break-words">
                <ReactMarkdown
                  components={{
                    h2: ({children}) => <h2 className="text-xl font-display font-semibold text-tp-text mt-8 mb-4 break-words">{children}</h2>,
                    h3: ({children}) => <h3 className="text-lg font-display font-semibold text-tp-text mt-6 mb-3 break-words">{children}</h3>,
                    p: ({children}) => <p className="text-tp-text-light leading-relaxed mb-4 break-words">{children}</p>,
                    ul: ({children}) => <ul className="list-disc pl-5 sm:pl-6 mb-4 text-tp-text-light">{children}</ul>,
                    li: ({children}) => <li className="mb-2 break-words">{children}</li>,
                    strong: ({children}) => <strong className="font-semibold text-tp-text">{children}</strong>,
                    a: ({href, children}) => <a href={href} className="text-tp-accent break-all hover:underline">{children}</a>,
                    hr: () => <hr className="my-8 border-tp-border" />
                  }}
                >
                  {content?.content || ''}
                </ReactMarkdown>
              </div>
              
              {content?.updated_at && (
                <p className="text-sm text-tp-text-light mt-8 pt-6 border-t border-tp-border">
                  Dernière mise à jour : {new Date(content.updated_at).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-tp-border py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-tp-text-light">
            &copy; {new Date().getFullYear()} {platformName}. Tous droits réservés.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MentionsLegalesPage;
